<template>
  <div class="choice-card-list w-100">
    <label v-if="itemModel.vars.label" :class="'form-label' + (itemModel.vars.required ? ' required' : '')">
      {{ itemModel.vars.label }}{{ itemModel.vars.required ? "*" : "" }}
      <span
        v-if="itemModel.vars.help && !isMobile()"
        v-tooltip="{ title: itemModel.vars.help, textWrap: true }"
        class="form-info ms-1"
      >
        <FontAwesomeIcon icon="fa-light fa-circle-question" />
      </span>
    </label>
    <div v-if="isMobile() && itemModel.vars.help" class="form-info">
      <FontAwesomeIcon icon="fa-light fa-circle-question" />
      {{ itemModel.vars.help }}
    </div>

    <div class="choice-card-list__search">
      <FontAwesomeIcon icon="fa-light fa-magnifying-glass" />
      <InputTypeComponent :model="searchModel" />
    </div>

    <div class="choice-card-list__hint">
      <FontAwesomeIcon icon="fa-light fa-circle-info" />
      <span>{{ selectedCountLabel }}</span>
      <span class="choice-card-list__totalCount">{{ totalCountLabel }}</span>
    </div>

    <div class="choice-card-list__list">
      <div v-if="!visibleChoices.length" class="choice-card-list__empty">
        {{ $t(i18nKeys.empty) }}
      </div>
      <div
        v-for="choice in visibleChoices"
        :key="choice.value"
        class="choice-card-list__card"
        :class="{ '-selected': isSelected(choice), '-disabled': isChoiceDisabled(choice) }"
      >
        <label :for="checkboxId(choice)" class="choice-card-list__thumbLabel">
          <div
            class="choice-card-list__thumb"
            :style="{ background: primaryTint(choice), color: primaryColor(choice) }"
          >
            <FontAwesomeIcon :icon="primaryIcon(choice)" />
          </div>
        </label>
        <div class="choice-card-list__info">
          <label :for="checkboxId(choice)" class="choice-card-list__infoLabel">
            <span class="choice-card-list__body">
              <span class="choice-card-list__name">{{ choice.label }}</span>
              <span class="choice-card-list__meta">
                <BadgeComponent
                  v-for="tag in secondaryTags(choice)"
                  :key="tag.id"
                  tag="span"
                  :text="tag.name"
                  :dot="false"
                  :className="tag.color"
                  compact
                />
                <span v-if="choice.attr && choice.attr['data-detail-label']" class="choice-card-list__detail-tag">
                  {{ choice.attr["data-detail-label"] }}
                </span>
                <span v-if="choice.attr && choice.attr['data-adapted-to-level']" class="choice-card-list__reco-tag">
                  <FontAwesomeIcon icon="fa-light fa-check" />
                  {{ $t(i18nKeys.adaptedToLevel) }}
                </span>
              </span>
            </span>
          </label>
          <button
            v-if="choice.attr && choice.attr['data-more-info']"
            type="button"
            class="choice-card-list__more"
            :disabled="isDisabled || itemModel.vars.loadingChoiceValue === choice.value"
            @click.stop="$emit('openModal', choice)"
          >
            {{ $t(i18nKeys.moreInfo) }}
            <FontAwesomeIcon
              :icon="
                itemModel.vars.loadingChoiceValue === choice.value ? 'fa-light fa-spinner' : 'fa-light fa-chevron-right'
              "
              :spin="itemModel.vars.loadingChoiceValue === choice.value"
            />
          </button>
        </div>
        <input
          :id="checkboxId(choice)"
          type="checkbox"
          class="choice-card-list__checkInput visually-hidden"
          :checked="isSelected(choice)"
          :disabled="isChoiceDisabled(choice)"
          :aria-label="(isSelected(choice) ? $t(i18nKeys.selected) : $t(i18nKeys.select)) + ' – ' + choice.label"
          @change="toggle(choice)"
        />
        <label :for="checkboxId(choice)" class="choice-card-list__checkLabel" aria-hidden="true">
          <span class="choice-card-list__check">
            <FontAwesomeIcon v-if="isSelected(choice)" icon="fa-light fa-check" />
          </span>
        </label>
      </div>
      <button
        v-if="remainingCount > 0"
        type="button"
        class="choice-card-list__loadMore"
        :disabled="isDisabled || itemModel.vars.loadingMore"
        @click="showMore"
      >
        <FontAwesomeIcon v-if="itemModel.vars.loadingMore" icon="fa-light fa-spinner" spin />
        {{
          $tc(i18nKeys.loadMore, nextPageSize, {
            count: nextPageSize,
            remaining: remainingCount,
          })
        }}
      </button>
    </div>

    <div v-if="itemModel.vars.error" class="invalid-feedback d-block">
      {{ itemModel.vars.error }}
    </div>

    <BlockLoader :loading="!!itemModel.loading" />
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import InputTypeComponent from "@/components/form/InputTypeComponent.vue";
import BlockLoader from "@/components/blocks/BlockLoader.vue";
import BadgeComponent from "@/components/utils/badge/BadgeComponent.vue";
import { colorManager, ICON_FALLBACK_COLOR } from "@/services/platform/color-manager";
import { notificationManager } from "@/services/utilities/notification-manager";
import { formValidator } from "@/services/form/form-validator";
import { choiceIdentityManager } from "@/services/form/choice-identity-manager";

const FALLBACK_ICON = "fa-light fa-graduation-cap";
const TINT_ALPHA = 0.15;
const PAGE_SIZE = 5;

export default {
  name: "ChoiceCardListTypeComponent",
  components: { FontAwesomeIcon, BadgeComponent, BlockLoader, InputTypeComponent },
  props: {
    model: {
      type: Object,
      required: true,
    },
  },
  emits: ["update", "openModal", "loadMore"],
  data() {
    return {
      itemModel: this.model,
      searchModel: {
        vars: {
          name: `${this.model.vars.name}Search`,
          id: `${this.model.vars.name}-search`,
          value: "",
          required: false,
          disabled: false,
          block_prefixes: ["filter", "text"],
          label: "",
          label_attr: { class: "visually-hidden" },
          attr: { placeholder: "" },
        },
      },
    };
  },
  computed: {
    search: {
      get() {
        return this.searchModel.vars.value ?? "";
      },
      set(value) {
        this.searchModel.vars.value = value;
      },
    },

    choices() {
      return Object.values(this.itemModel.vars.choices || []);
    },

    selectedValues() {
      return this.itemModel.vars.value || [];
    },

    maxCount() {
      return this.itemModel.vars.attr?.["data-max-count"] || Infinity;
    },

    isDisabled() {
      return !!(this.itemModel.vars.disabled || (this.itemModel.vars.attr && this.itemModel.vars.attr.readonly));
    },

    i18nKeys() {
      return this.itemModel.vars.i18nKeys || {};
    },
    searchPlaceholder() {
      return this.$t(this.i18nKeys.searchPlaceholder);
    },

    selectedCountLabel() {
      return Number.isFinite(this.maxCount)
        ? this.$tc(this.i18nKeys.selectedCount, this.selectedValues.length, {
            count: this.selectedValues.length,
            max: this.maxCount,
          })
        : this.$tc(this.i18nKeys.selectedCountNoMax, this.selectedValues.length, {
            count: this.selectedValues.length,
          });
    },
    totalCountLabel() {
      return this.$tc(this.i18nKeys.totalCount, this.total, { total: this.total });
    },

    total() {
      return this.itemModel.vars.total ?? this.itemModel.vars.attr?.["data-total"] ?? this.choices.length;
    },

    nextPageSize() {
      return Math.min(this.itemModel.vars.pageSize ?? PAGE_SIZE, this.remainingCount);
    },
    remainingCount() {
      return Math.max(this.total - this.choices.length, 0);
    },

    visibleChoices() {
      const byIdentity = new Map(this.choices.map((choice) => [choice.value, choice]));
      this.selectedValues.forEach((value) => {
        const identity = choiceIdentityManager.identity(value);
        if (identity !== null && !byIdentity.has(identity)) {
          byIdentity.set(identity, {
            label: value.name ?? value.commercialName ?? "",
            value: identity,
            data: value,
            attr: value.attr || {},
          });
        }
      });
      let list = Array.from(byIdentity.values());
      const term = this.search.trim().toLowerCase();
      if (term) {
        list = list.filter((choice) => choice.label.toLowerCase().includes(term));
      }
      return list;
    },
  },
  watch: {
    searchPlaceholder: {
      immediate: true,
      handler(placeholder) {
        this.searchModel.vars.attr.placeholder = placeholder;
        this.searchModel.vars.label = placeholder;
      },
    },
    isDisabled: {
      immediate: true,
      handler(disabled) {
        this.searchModel.vars.disabled = disabled;
      },
    },
  },
  methods: {
    isMobile() {
      return window.innerWidth < 992;
    },
    checkboxId(choice) {
      return `${this.itemModel.vars.name}-check-${choice.value}`;
    },
    showMore() {
      this.$emit("loadMore");
    },
    secondaryTags(choice) {
      return (choice.attr && choice.attr["data-tags"]) || [];
    },
    primaryColor(choice) {
      const tags = this.secondaryTags(choice);
      return tags[0]?.color || ICON_FALLBACK_COLOR;
    },
    primaryIcon(choice) {
      const tags = this.secondaryTags(choice);
      return tags[0]?.icon ? "fa-light fa-" + tags[0].icon : FALLBACK_ICON;
    },
    tint(hex) {
      return colorManager.hexWithAlpha(hex || ICON_FALLBACK_COLOR, TINT_ALPHA);
    },
    primaryTint(choice) {
      return this.tint(this.primaryColor(choice));
    },
    isSelected(choice) {
      return this.selectedValues.some((value) => choiceIdentityManager.identity(value) === choice.value);
    },
    isChoiceDisabled(choice) {
      return this.isDisabled || (!this.isSelected(choice) && this.selectedValues.length >= this.maxCount);
    },
    toggle(choice) {
      if (this.isSelected(choice)) {
        this.itemModel.vars.value = this.selectedValues.filter(
          (value) => choiceIdentityManager.identity(value) !== choice.value,
        );
      } else if (this.selectedValues.length < this.maxCount) {
        this.itemModel.vars.value = [...this.selectedValues, choice.data];
      } else {
        notificationManager.showNotification(
          "info",
          this.$t(this.i18nKeys.maxSelectionReached, { max: this.maxCount }),
        );
        return false;
      }
      formValidator.validModel(this.itemModel);
      this.$emit("update", this.itemModel.vars.value);
      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./choice-list-shared.scss";
$check-size: 18px;
$check-offset: 9px;

.choice-card-list {
  position: relative;

  &__search {
    @extend %choice-search;
  }

  &__hint {
    font-size: 11px;
    color: $dark-grey;
    margin: 14px 0 9px;
    display: flex;
    align-items: center;
    gap: 6px;

    @include dark-theme {
      color: $light-grey;
    }
  }

  &__totalCount {
    margin-left: auto;
    font-weight: 700;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 9px;
  }

  &__loadMore {
    @extend %choice-expand-btn;
  }

  &__empty {
    @extend %choice-empty;
    padding: 22px 14px;
    font-size: 12px;
    background: $bg-grey;
    border-radius: 10px;

    @include dark-theme {
      background: $dark-theme-bg;
    }
  }

  &__card {
    border: 1.5px solid $m-color_6;
    border-radius: 12px;
    padding: 11px;
    display: flex;
    gap: 11px;
    position: relative;
    background: $white;
    transition: 0.15s;

    @include dark-theme {
      background: $dark-theme-bg;
      border-color: $m-color_12;
    }

    &.-selected {
      border-color: $success;
      background: rgba($success, 0.1);

      @include dark-theme {
        background: rgba($success, 0.1);
        border-color: $success;
      }
    }

    &.-disabled {
      opacity: 0.5;
      cursor: not-allowed;

      .choice-card-list__thumbLabel,
      .choice-card-list__info,
      .choice-card-list__body,
      .choice-card-list__check {
        cursor: not-allowed;
      }
    }
  }

  &__thumbLabel {
    display: contents;
    cursor: pointer;
  }

  &__thumb {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__info {
    flex: 1;
    min-width: 0;
    padding-right: $check-offset + $check-size - 1px;
  }

  &__infoLabel {
    display: block;
    cursor: pointer;
  }

  &__body {
    display: block;
    width: 100%;
    text-align: left;
  }

  &__name {
    font-size: 13px;
    font-weight: 600;
    color: $primary;
    line-height: 1.25;

    @include dark-theme {
      color: $white;
    }
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-top: 7px;
    flex-wrap: wrap;
  }

  &__detail-tag {
    font-size: 10px;
    font-weight: 600;
    color: $primary;
    background: $bg-grey;
    padding: 2px 7px;
    border-radius: 6px;

    @include dark-theme {
      background: $dark-theme-bg;
      color: $white;
    }
  }

  &__reco-tag {
    font-size: 10px;
    font-weight: 600;
    color: darken($landing-green-dark, 18%);
    background: rgba($landing-green-dark, 0.12);
    padding: 2px 7px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    gap: 4px;

    svg {
      width: 9px;
    }
  }

  &__more {
    font-size: 10.5px;
    font-weight: 600;
    color: $primary;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin-top: 7px;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
    font-family: inherit;

    @include dark-theme {
      color: $white;
    }

    svg {
      width: 10px;
    }
  }

  &__checkInput {
    &:focus-visible + .choice-card-list__checkLabel .choice-card-list__check {
      outline: 2px solid $primary;
      outline-offset: 2px;
    }
  }

  &__checkLabel {
    display: contents;
    cursor: pointer;
  }

  &__check {
    position: absolute;
    top: $check-offset;
    right: $check-offset;
    width: $check-size;
    height: $check-size;
    border-radius: 50%;
    border: 2px solid $m-color_6;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $white;
    flex-shrink: 0;
    cursor: pointer;
    padding: 0;
    color: $white;
    font-size: 10px;

    @include dark-theme {
      background: $dark-theme-bg;
      border-color: $m-color_12;
    }
  }

  &__card.-selected &__check {
    background: $success;
    border-color: $success;
  }
}
</style>
